<template>
  <ProvideSearch
    v-slot="{ sorted }"
    :records="entries"
    :keys="searchKeys"
    :search-pattern="searchInput"
    :disabled="inApiMode"
  >
    <!--
     Triggered on change to search or sorting has occurred
     @event input
     @property {Array} records - The set of active records currently displayed in the table
   -->
    <ProvidePagination
      v-slot="{ activePage, changePage, changePerPage, perPage, totalItems, records }"
      :records="sorted"
      :per-page="pageLength"
      :paginator-info="paginatorInfo"
      @page-update="handleUpdatePage"
      @per-page-update="handleUpdatePageLength"
      @change="$emit('input', $event)"
    >
      <ProvideLazyRendering
        v-slot="{ lazyRecords }"
        :records="records"
        :root="$refs.tbody"
        :trigger="$refs.trigger"
      >
        <div v-bind="$attrs" class="table-responsive" :class="{ 'tw-h-full': !slim }">
          <!-- @slot To replace the control bar entirely -->
          <slot
            name="control-bar"
            :update-search="(value) => (searchInput = value)"
            :search="searchInput"
            :selected-count="selectedCount"
            :page-length="pageLength"
            :page-length-options="pageLengthOptions"
          >
            <BaseDataTableControlBar
              v-if="!slim"
              :can-select-all="multiSelect"
              :selected-count="selectedCount"
              :total-count="entries.length"
              :page-length="pageLength"
              :page-length-options="pageLengthOptions"
              :search-disabled="searchDisabled"
              :select-all-disabled="!entries.length"
              :search-placeholder="searchPlaceholder"
              @toggle-all="toggleAll(records)"
              @search="handleSearch"
            >
              <template #left-aligned>
                <!-- @slot To add items inside the control bar, for example a bulk actions button -->
                <slot name="additional-control-bar-elements" />
              </template>
              <template #right-aligned>
                <!-- @slot To add items inside the control bar to the right, for example a custom search -->
                <slot name="additional-control-bar-elements-right" />
              </template>
            </BaseDataTableControlBar>
          </slot>
          <div
            class="tw-relative"
            :style="!slim ? `height: calc(${maxHeight} - 68px);` : ''"
            :class="{ stickyShadow }"
          >
            <div v-if="loading" class="loading-overlay" />
            <div
              ref="tbody"
              class="tw-relative tw-max-w-full tw-h-full tw-max-h-full table-wrap m-t-10 tw-min-w-full tw-inline-block"
              :class="{ 'tw-h-[100vh]': !slim }"
            >
              <div class="tw-min-w-full tw-w-max tw-min-h-full tw-h-max">
                <table class="table" style="width: 100%">
                  <!-- @slot To replace content inside `<table>` -->
                  <slot name="table-content">
                    <thead>
                      <!-- @slot To replace content inside `<thead>` -->
                      <slot name="thead" :headers="headers">
                        <!-- @TODO HANDLE SORTING ON THIS ^ SLOT -->
                        <tr>
                          <!-- @slot To replace content inside `<thead><tr>` -->
                          <slot name="thead-tr">
                            <!-- @TODO HANDLE SORTING ON THIS ^ SLOT -->
                            <th
                              v-for="col of headers"
                              :key="col.key"
                              :class="{
                                ...col.colClass,
                                'sort-enabled': col.orderAble,
                                [ordering[col.key]]: ordering[col.key],
                                'sticky-enabled': col.sticky,
                                'tw-pointer': col.multiSelect,
                              }"
                              :data-test="`column-${col.key}`"
                              @click="handleOrder(col)"
                            >
                              <div
                                :class="{
                                  'tw-flex': true,
                                  'tw-items-center': true,
                                  ...col.flexClass,
                                }"
                              >
                                <slot :name="'Head-' + col.slot" :col="col">
                                  {{ $t(col.title || '') }}
                                </slot>
                                <div v-if="col.multiselect" @click="toggleAll(records)">
                                  <WdsCheckbox
                                    :checked="
                                      selectedCount >= records.length &&
                                      !loading &&
                                      selectedCount !== 0
                                    "
                                    @click.prevent
                                  />
                                </div>
                                <div class="tw-flex-shrink-1 tw-basis-2"></div>
                                <WdsIcon
                                  v-if="col.orderAble"
                                  class="sort-icon tw-flex-shrink-0"
                                  name="chevron-up"
                                  :size="16"
                                />
                              </div>
                            </th>
                          </slot>
                        </tr>
                      </slot>
                    </thead>
                    <tbody>
                      <!-- @slot To replace content inside `<tbody>` -->
                      <slot name="tbody">
                        <tr
                          v-if="lazyRecords.length === 0"
                          class="text-center table-empty"
                          role="row"
                        >
                          <td
                            :colspan="headers.length"
                            :data-test="
                              loading
                                ? 'base-data-table-loading-indicator'
                                : 'base-data-table-no-results-indicator'
                            "
                          >
                            <!-- @slot For 'no results found' content -->
                            <slot name="no-results" :loading="loading" :search="searchInput">
                              {{ loading ? $t('Loading') : $t('No matching records found') }}
                            </slot>
                          </td>
                        </tr>
                        <BaseDataTableRowLazy
                          v-for="({ render, row }, ri) of lazyRecords"
                          :key="hash(JSON.stringify(row) + ri)"
                          :initial-render="render"
                          :class="getRowClass(ri, lazyRecords)"
                          :tall="tallRows"
                        >
                          <!-- @slot To replace content inside `<tbody><tr>` <br>@bindings rowData, rowKey -->
                          <slot name="tbody-tr" :row-data="row" :row-key="ri">
                            <td
                              v-for="(col, ci) of headers"
                              :key="hash(JSON.stringify(col) + JSON.stringify(row) + ci)"
                              :class="[
                                col.class ? col.class : '',
                                col.sticky ? 'sticky-enabled' : '',
                              ]"
                              @click="!col.ignoreClickForRowSelection && rowSelect(row)"
                            >
                              <!-- Use a specific component if one was provided in the columns config -->
                              <component
                                v-bind="col.renderUsing?.componentProps?.(row)"
                                :is="col.renderUsing.component"
                                v-if="
                                  col.renderUsing &&
                                  col.renderUsing.component &&
                                  (col.renderUsing.shouldRender ?? ((row) => true))(row)
                                "
                              />
                              <!-- If no component was specified, use the named slot with raw data as a fallback -->
                              <slot
                                v-else
                                :col="col"
                                :row="row"
                                :row-index="ri"
                                :name="col.slot ? col.slot : col.title"
                                :is-row-selected="!!selectedRows[hash(row)]"
                              >
                                <template v-if="col.slot === 'Select'">
                                  <WdsCheckbox
                                    :checked="!!selectedRows[hash(row)]"
                                    @click.prevent
                                  />
                                </template>
                                <!-- If no slot template was provided, render the raw data -->
                                <template v-else>
                                  {{
                                    row[col.key] !== null && row[col.key] !== undefined
                                      ? row[col.key]
                                      : ''
                                  }}
                                </template>
                              </slot>
                            </td>
                          </slot>
                        </BaseDataTableRowLazy>
                      </slot>
                    </tbody>
                  </slot>
                </table>
              </div>
              <div ref="trigger"></div>
            </div>
          </div>
          <WdsPagination
            v-if="!slim"
            :page="activePage - 1"
            :number-of-items="totalItems"
            :items-per-page="perPage"
            class="tw-py-4 tw-px-4"
            @update:page="changePage"
            @update:per-page="changePerPage"
          />
        </div>
      </ProvideLazyRendering>
    </ProvidePagination>
  </ProvideSearch>
</template>

<script>
/* eslint-disable no-restricted-syntax,vue/no-reserved-keys */
import { WdsPagination, WdsCheckbox, WdsIcon } from '@worksome/wds-components'
import { useScroll } from '@vueuse/core'
import { computed, onUpdated, ref, toRefs, useTemplateRef, watch } from 'vue'
import HashMixin from '@/mixins/MethodHash'
import BaseDataTableControlBar from '@/components/BaseDataTable/BaseDataTableControlBar.vue'
import ProvidePagination from '@/components/ProvidePagination.vue'
import ProvideSearch from '@/components/ProvideSearch.vue'
import BaseDataTableRowLazy from '@/components/BaseDataTable/BaseDataTableRowLazy.vue'
import ProvideLazyRendering from '@/components/ProvideLazyRendering.vue'
import { graphqlFragmentValidator } from '@/utils/propValidation'
import { textHelpers, debounce } from '@/utils/tools'

export default {
  name: 'BaseDataTable',
  components: {
    WdsPagination,
    WdsIcon,
    WdsCheckbox,
    BaseDataTableControlBar,
    ProvidePagination,
    ProvideSearch,
    BaseDataTableRowLazy,
    ProvideLazyRendering,
  },
  mixins: [HashMixin],
  inheritAttrs: false,
  fragments: {
    pagination: ProvidePagination.fragments.pagination,
  },
  props: {
    /**
     * Array of row data objects which all have the same keys
     */
    entries: {
      type: Array,
      default: () => [],
    },
    /**
     * Definitions of each column, and the access points for each column in the rows
     * Each column needs to contain at least a title prop, and a key prop ie: {key: 'foo', title: 'bar'}
     * Other options are
     * {
     *   class | row cell class
     *   colClass | column header class
     *   flexClass | column flex classes. Allows to control the classes on the headers flex div
     *   order | the value used to order a column, can be a function(row) but is primarily a key for the row object
     *   slot | name of the slot, if no slot is added title is used instead,
     *          if added, can also access the column slot at Head-${slot}
     *   orderAble | makes the column abel to be ordered in asc or desc
     *   sort | the path to the value to sort and search the column by ie sort.by.this.value if the row object
     *          is more deep. If not provided the key will be used instead.
     *   searchKeys | A list of search keys for fuse.js to use. If not provided, the key will be used instead
     *   hide | if true, the column won't be rendered
     *   sticky | "sticks" the column so that it always visible on the table. Best used for action menus that can be
     *            hidden by overflow on smaller screens
     * }
     */
    columns: {
      type: Array,
      default: () => [],
    },

    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Ability to select multiple rows
     * For selected rows listen on the @rows-selected event
     */
    multiSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * Pre-selected rows (when using multi-select)
     */
    preSelectedRows: {
      type: Array,
      default: () => [],
    },

    /**
     * Results from lighthouses @pagination directive.
     * If given and validated this switches the datatable into gql mode where inputs are not applied to the entries
     * but only emitted for the implementation component to handle sorting pagination and so forth
     */
    paginatorInfo: {
      type: Object,
      default: undefined,
      validator: graphqlFragmentValidator(ProvidePagination.fragments.pagination),
    },
    /**
     * Multiselect will not be triggered by clicking on the last td on each row
     * Useful for things like dropdown menus & action buttons in the last column
     */
    preventRowSelectionOnLastTd: {
      type: Boolean,
      default: false,
    },
    /**
     * Dont use search, pagination, etc
     */
    slim: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines which key to use when building the array for @selection-updated event for multi-select
     * fx setting this to 'name', would emit ["Danny", "Spaghetti Sam"]
     */
    multiSelectTargetKey: {
      type: String,
      default: 'globalId',
    },
    defaultPageLength: {
      type: Number,
      default: 50,
    },
    defaultPageLengthOptions: {
      type: Array,
      default: () => [10, 50, 500],
    },
    searchDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Search placeholder text
     */
    searchPlaceholder: {
      type: String,
      default: $t('Search'),
    },
    /**
     * Determines whether or not to preserve the rows that are selected when the entries prop changes
     * ex. when filtering a table and a new entries reference is created but the selected rows are still relevant
     */
    preserveSelectedRows: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether to use default table row height or taller table row height.
     */
    tallRows: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines whether to apply a hover effect when hovering rows.
     */
    rowHover: {
      type: Boolean,
      default: false,
    },

    forceSearchInApiMode: {
      type: Boolean,
      default: false,
    },

    /**
     * This points a prop on **ALL** rows that uniquely identifies the row. Can massively boost performance as it skips
     * hashing and string encoding to generate keys.
     */
    rowUuid: {
      type: String,
      default: null,
    },

    maxHeight: {
      type: String,
      default: '70vh',
    },
  },
  emits: ['search', 'input', 'rows-selected', 'selection-updated', 'row-click'],
  setup(props) {
    const { loading } = toRefs(props)
    const tbody = useTemplateRef('tbody')
    const { arrivedState, isScrolling } = useScroll(tbody)
    const { right } = toRefs(arrivedState)

    // This part checks handles the logic where we load in elements for the first time before any content has been added
    // the right scroll value does not get updated, so we do a manual check after each tune the content is updated
    // lastly we only want this to have effect once since the right value handles it afterwards.
    const hasScrolled = ref(false)
    const hasScrollBars = ref(false)
    watch(
      isScrolling,
      () => {
        hasScrolled.value = true
      },
      { once: true },
    )

    onUpdated(() => {
      hasScrollBars.value = tbody.value?.scrollWidth > tbody.value?.offsetWidth
    })

    const stickyShadow = computed(
      () => (!loading.value && !right.value) || (hasScrollBars.value && !hasScrolled.value),
    )

    return { stickyShadow }
  },
  data() {
    // This loop ensures that vue is properly reactive to changes to the preselected rows that we might have
    const selectedRows = {}
    for (const row of this.preSelectedRows) {
      selectedRows[this.hash(row)] = row
    }

    return {
      searchInput: '',
      pageLength: this.defaultPageLength,
      pageLengthOptions: this.defaultPageLengthOptions,
      page: 0,
      height: visualViewport.height - 350,
      // Is formatted as { [col.key]: col.order }
      ordering: Object.assign({}, ...this.columns.map((col) => ({ [col.key]: col.order }))),
      selectedRows,
      inApiMode: false,
    }
  },
  computed: {
    /**
     * Headers will be the same as the columns prop
     * or interpreted from the first element in the :entries prop
     * @returns {*[]|*[]}
     */
    headers() {
      let headers
      if (this.columns.length) {
        headers = this.columns
      } else {
        headers = this.entries.length
          ? Object.keys(this.entries?.[0]).map((key) => ({
              key,
              title: textHelpers.keyToHumanReadable(key),
              orderAble: true,
            }))
          : null
      }
      // disable hidden items
      headers = headers?.filter((x) => !x._col?.hide) ?? []
      return this.multiSelect
        ? [{ slot: 'Select', title: '', multiselect: true, orderAble: false }, ...headers]
        : headers
    },
    searchKeys() {
      let keys = []
      for (const c of this.headers.filter((col) => col.orderAble !== false)) {
        if ((c.searchKeys ?? []).length !== 0) {
          keys = [
            ...keys,
            ...c.searchKeys.map((sk) => {
              const searchKey = { name: sk }
              return {
                name: searchKey.name,
                sort: this.ordering[sk.sort ?? searchKey.name],
                convert: sk.convert,
                getFn: c?._col?.getFn,
                type: sk.type,
              }
            }),
          ]
        } else {
          keys.push({
            name: c.sort ?? c.key,
            sort: this.ordering[c.key],
            convert: c.convert,
            getFn: c?._col?.getFn,
            type: c.type,
          })
        }
      }

      return keys
    },
    selectedCount() {
      return Object.values(this.selectedRows).length
    },
    searchSettings: {
      get() {
        return {
          page: this.page,
          perPage: this.pageLength,
          search: this.searchInput,
          order: this.ordering,
        }
      },
      set({ page, perPage, search, order }) {
        this.page = page ?? this.page ?? 0
        this.pageLength = perPage ?? this.pageLength ?? 50
        this.searchInput = search ?? this.searchInput ?? ''

        function nextOrderDirection(dir) {
          switch (dir) {
            case undefined:
              return 'asc'
            case 'asc':
              return 'desc'
            default:
              return 'asc'
          }
        }
        // Might want to figure out a way where ordering takes priority somehow,
        // but not sure how or if the backend supports that
        if (order) {
          const keys = []
          for (const o of order) {
            this.ordering[o.key] = nextOrderDirection(this.ordering[o.key])
            keys.push(o.key)
          }
          // This part removes any currently selected sorting options that are not specified
          for (const key in this.ordering) {
            if (!keys.includes(key)) this.ordering[key] = undefined
          }
        }
        /**
         * Whenever the search settings changes this event gets fired
         * These changes can be search in the search bar, ordering or pagination changes.
         * @event search
         * @property {Object} searchSettings - the current active settings describing the search state of the table
         */
        this.$emit('search', this.searchSettings)
      },
    },
    shouldHoverRow() {
      return this.rowHover ? this.rowHover : this.multiSelect
    },
  },
  watch: {
    // Debounce the search if we are in api mode
    // Cant do in created() because this.paginatorInfo is not initially filled
    paginatorInfo(newValue, oldValue) {
      // Ensure code is only run once when paginatorInfo received
      if (!oldValue && newValue && !this.inApiMode) {
        // Since we received paginatorInfo, we are using api mode
        this.inApiMode = !this.forceSearchInApiMode
        this.handleSearch = debounce(this.handleSearch, 600)
      }
    },
    columns: {
      deep: true,
      handler() {
        this.ordering = Object.assign({}, ...this.columns.map((col) => ({ [col.key]: col.order })))
      },
    },

    entries: {
      deep: true,
      handler() {
        if (!this.preserveSelectedRows) {
          this.selectedRows = {}
        }
      },
    },
    preSelectedRows(rows) {
      for (const row of rows) {
        this.selectedRows[this.getKey(row)] = row
      }
    },
  },
  created() {
    window.addEventListener('resize', () => {
      this.height = visualViewport.height - 350
    })
  },
  methods: {
    toggleAll(data) {
      // We check if all of the rows are already in the selected rows, in which case we remove them to clear
      // the paginated/filtered rows
      if (data.every((row) => !!this.selectedRows[this.getKey(row)])) {
        // Remove them all as all are selected
        for (const row of data) {
          delete this.selectedRows[this.getKey(row)]
        }
        // Works like Vue.set but is manually triggered here as Vue.set does not handle delete operations
        this.emitUpdates()
        return
      }

      for (const row of data) {
        this.selectedRows[this.getKey(row)] = row
      }
      this.emitUpdates()
    },
    getKey(row) {
      return this.rowUuid ? row?.[this.rowUuid] : this.hash(row)
    },
    handleSearch(search) {
      // Do search n stuff
      this.searchSettings = { search }
    },
    handleUpdatePageLength(perPage) {
      this.searchSettings = { perPage }
    },
    handleUpdatePage(page) {
      this.searchSettings = { page }
    },
    handleOrder(col) {
      if (col.orderAble) {
        this.searchSettings = { order: [col] }
      }
    },
    emitUpdates() {
      /**
       * Anytime changes occur to the rows
       *
       * @event rows-selected
       * @property {Array} selectedRows - The list of current rows to have been selected
       */
      this.$emit('rows-selected', this.selectedRows)
      /**
       * Anytime changes occur to the rows
       *
       * @event selection-updated
       * @property {Array} selectedRowIDs - Array of the current selected row global ids
       */
      this.$emit(
        'selection-updated',
        Object.values(this.selectedRows).map(
          (dataPoint) => dataPoint[this.multiSelectTargetKey] || '',
        ),
      )
    },
    rowSelect(row) {
      /**
       * @event row-click
       * @property {Object} row - The row that has been clicked
       */
      this.$emit('row-click', row)
      if (this.multiSelect) {
        const hash = this.getKey(row)
        if (this.selectedRows[hash] !== undefined) {
          delete this.selectedRows[hash]
        } else {
          this.selectedRows[hash] = row
        }
      }
      this.emitUpdates()
    },
    getRowClass(rowIndex, records) {
      const rowClass = this.shouldHoverRow ? 'row-hover' : ''

      if (this.selectedRows[this.getKey(records[rowIndex]?.row)]) {
        // Checks the next in line row to see if it has been selected, if so we don't round the corners on the bottom
        // edge since that looks kinda weird. Perhaps this logic could be replaced with some smart css?
        if (
          records[rowIndex + 1]?.row &&
          this.selectedRows[this.getKey(records[rowIndex + 1]?.row)]
        ) {
          return `${rowClass} selected bottom-straight`
        }
        return `${rowClass} selected`
      }
      return rowClass
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';
.btn {
  white-space: normal;
}

table.dataTable thead [class*='sorting'] {
  background-position: center right 0px;
}

table {
  border-collapse: collapse;
}
th {
  position: sticky;
  top: 0;
  min-width: 50px;
  background: white;
  z-index: 4;
}

.loading-overlay {
  top: 5px;
  left: 0;
  right: 0;
  bottom: 5px;
  z-index: 20;
  position: absolute;
  cursor: progress;
  overflow: hidden;
  opacity: 0.2;
  background-color: lightgray;
}

// Table sorting
.sort-enabled {
  @apply tw-select-none;
  @apply tw-cursor-pointer;
  &:hover,
  &.asc,
  &.desc {
    svg {
      @apply tw-visible;
    }
  }

  &:hover:not(.asc):not(.desc) {
    svg {
      @apply tw-text-gray-400;
    }
  }

  &.desc {
    svg {
      @apply tw-rotate-180;
    }
  }
}

// Mixin for common sticky behavior
@mixin sticky-cell {
  position: sticky;
  right: 0;
  z-index: 2;

  &:focus-within {
    z-index: 3;
  }
}

// Apply the mixin for both table headers and cells,
// with overrides for individual differences.
th.sticky-enabled {
  @include sticky-cell;
  z-index: 19;
}

td.sticky-enabled {
  @include sticky-cell;
  background-color: white;
}

// Nesting the pseudo-element styles under .stickyShadow
.stickyShadow {
  .sticky-enabled {
    &::before {
      content: '';
      position: absolute;
      left: -10px; // adjust based on desired shadow width
      top: 0;
      bottom: -1px;
      width: 10px;
      pointer-events: none;
      box-shadow: inset -10px 0px 10px -8px rgba(0, 0, 0, 0.1);
    }
  }

  // Override the bottom value specifically for th
  th.sticky-enabled {
    &::before {
      bottom: -8px;
    }
  }
}

.sort-icon {
  @apply tw-invisible;
  @apply tw-inline #{!important};
  @apply tw-transition-transform;
}

// Table header
thead > tr > th {
  @apply tw-bg-true-gray-50;
  @apply tw-text-[13px];
  @apply tw-text-gray-600;
  @apply tw-border-b-0;

  &:first-child {
    @apply tw-rounded-l-lg;
  }

  &:last-child {
    @apply tw-rounded-r-lg;
  }
}

// Table rows
tbody > tr {
  &:first-child > td {
    border-top: 1px solid transparent;
  }
}

tbody > tr:not(.table-empty) {
  &:last-child > td {
    @apply tw-pb-2;
    border-bottom: 1px solid $gray-100;
  }

  &:not(:first-child) > td {
    border-top: 1px solid $gray-100;
  }

  > td:not(.skip-font-adjustments),
  td:not(.skip-font-adjustments) :deep(.wds-text) {
    @apply tw-align-middle;
    @apply tw-text-[13px];
    @apply tw-text-gray-850;
  }

  &.selected {
    &.row-hover:hover {
      > td {
        @apply tw-bg-indigo-50;
      }
    }

    > td {
      @apply tw-bg-[#F8F6FF];
      border-bottom: 1px solid transparent;
      border-top: 1px solid transparent;

      &:first-child {
        @apply tw-rounded-l-lg;
      }

      &:last-child {
        @apply tw-rounded-r-lg;
      }
    }

    + tr.selected {
      > td {
        &:first-child {
          @apply tw-rounded-tl-none;
        }

        &:last-child {
          @apply tw-rounded-tr-none;
        }
      }
    }
  }

  &.selected.bottom-straight {
    > td {
      &:first-child {
        @apply tw-rounded-bl-none;
      }

      &:last-child {
        @apply tw-rounded-br-none;
      }
    }
  }

  &.row-hover:hover:not(.selected) {
    > td {
      @apply tw-bg-true-gray-50;

      &:first-child {
        @apply tw-rounded-l-lg;
      }

      &:last-child {
        @apply tw-rounded-r-lg;
      }

      border-bottom: 1px solid transparent;
      border-top: 1px solid transparent;
    }
  }
}

.table-wrap {
  overflow: auto;
}

tbody {
  &:before {
    @apply tw-content-[''];
    @apply tw-block;
    @apply tw-h-[7px];
  }
}
</style>
